/* eslint-disable no-empty */
import { AxiosInstance } from 'axios';

import NotificationService from 'modules/notifications/notificationService';
import { ADVERTISERS_URL, AGENCIES_URL, DSP_ADVERTISERS_URL } from 'modules/organization/constants';
import { VIEW_URL } from 'modules/performance/constants';
import { INVITE_USER_URL, UPDATE_MEMBER_URL, USERS_URL } from 'modules/user/constants';
import {
  INSERTION_ORDER_STATUS_URL_LIST,
  INSERTION_ORDER_URL,
  LINE_ITEM_EDIT_URL,
  XANDR_STATUS_URL,
} from 'modules/xandr/constants';

import { ROUTES } from 'routing/constants';

import { FILE_URL } from 'services/file/fileService';

import { DUPLICATE_ENTRY_CODE, HttpStatus } from './httpStatus';

export function addResponseInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    async (response) => {
      const status = response?.status;
      const requestedUrl = response?.request?.responseURL;
      const currentUrl = window.location.href;
      const method = response?.config?.method?.toUpperCase();

      if (status === HttpStatus.Success) {
        if (method === 'DELETE' && requestedUrl.includes(DSP_ADVERTISERS_URL)) {
          NotificationService.success('Advertiser deleted');
        } else if (requestedUrl.endsWith(FILE_URL)) {
          NotificationService.success('Image uploaded');
        } else if (
          (requestedUrl.endsWith(USERS_URL) && currentUrl.endsWith(ROUTES.USER_SETTINGS)) ||
          (requestedUrl.endsWith(UPDATE_MEMBER_URL) && currentUrl.endsWith(ROUTES.MEMBERS_SETTINGS))
        ) {
          NotificationService.success('User updated');
        } else if (requestedUrl.endsWith(ADVERTISERS_URL)) {
          NotificationService.success('Advertiser updated');
        } else if (
          requestedUrl.endsWith(AGENCIES_URL) &&
          currentUrl.endsWith(ROUTES.AGENCY_SETTINGS) &&
          method === 'PUT'
        ) {
          NotificationService.success('Agency updated');
        } else if (requestedUrl.endsWith(VIEW_URL) && currentUrl.endsWith(ROUTES.HOME)) {
          NotificationService.success('View updated');
        } else if (requestedUrl.endsWith(LINE_ITEM_EDIT_URL)) {
          NotificationService.success('Line item updated');
        } else if (requestedUrl.endsWith(INSERTION_ORDER_URL)) {
          NotificationService.success('Insertion order updated');
        }
      } else if (status === HttpStatus.Created) {
        if (requestedUrl.endsWith(INVITE_USER_URL)) {
          NotificationService.success('User invited');
        } else if (requestedUrl.endsWith(ADVERTISERS_URL)) {
          NotificationService.success('Advertiser created');
        } else if (requestedUrl.endsWith(AGENCIES_URL)) {
          NotificationService.success('Agency created');
        } else if (requestedUrl.endsWith(VIEW_URL)) {
          NotificationService.success('View created');
        } else if (requestedUrl.endsWith(INSERTION_ORDER_STATUS_URL_LIST)) {
          NotificationService.success('Synced to dashboard');
        }
      } else if (status === HttpStatus.NoContent) {
        if (requestedUrl.endsWith(FILE_URL)) {
          NotificationService.info('Image deleted');
        } else if (requestedUrl.includes(USERS_URL)) {
          NotificationService.success('User deleted');
        } else if (requestedUrl.includes(VIEW_URL)) {
          NotificationService.success('View deleted');
        }
      }

      return response;
    },
    async (error) => {
      const status = error?.response?.status;
      // `reason` is the user-facing text when the backend supplies one; `message` is generic.
      const message = error?.response?.data?.reason || error?.response?.data?.message;
      const code = error?.response?.data?.code;
      const requestedUrl = error.response?.request?.responseURL;

      switch (status) {
        case HttpStatus.Unauthorized:
          window.location.replace(ROUTES.LOGIN);
          break;

        case HttpStatus.Forbidden:
          break;

        case HttpStatus.BadRequest:
          if (code === DUPLICATE_ENTRY_CODE) {
            NotificationService.warn(
              'An item with this name already exists. Please choose a different name to avoid duplicates.',
            );
          } else {
            NotificationService.warn(message);
          }
          break;

        case HttpStatus.InternalServerError:
          // temporary workaround, because 500 for now is a normal response status where there is no xandr active connection
          if (!requestedUrl.includes(XANDR_STATUS_URL)) {
            NotificationService.error(message);
          }
          break;

        default:
          if (!error.response) {
            // console.log('No response received from server');
          } else {
            // console.error('Error in request setup:', error.message);
          }
      }

      return Promise.reject(error);
    },
  );
}
